import loadable from "@loadable/component"
import { FocusScope } from "@react-aria/focus"
import PropTypes from "prop-types"
import { useCallback, useEffect, useRef } from "react"
import { Helmet } from "react-helmet-async"
import { useDispatch, useSelector } from "react-redux"

import { setExperiment } from "app-store-v2/app/actions"
import fetchProduct from "areas/product/hoc/fetch-product"
import withImages from "areas/product/hoc/with-images"
import { trackSlide } from "areas/product/store/actions"
import SimpleDeferAfterLoadWrapper from "components/common/simple-defer-load-wrapper"
import Button from "components/ui/button"
import { Carousel, CarouselContent, CarouselItem } from "components/ui/carousel"
import Image from "components/ui/image"
import Media from "components/ui/media"
import Shimmer from "components/ui/shimmer"
import { IS_JEWLR, retry, isTestMode } from "helpers/application"
import { isVideoView, isJ3dView } from "helpers/gallery-slide-view"
import scrollToElement from "helpers/scroll-to-element"
import { useNavigation } from "helpers/use-navigation"
import useWindowDimensions from "helpers/window-dimensions"
import fetchWishlist from "hoc/fetch-wishlist"
import { cn } from "styles/utils"

import ImageAreaContent from "./image-area-content"
import MainBody from "./main-body"
import Breadcrumbs from "./wizard-header/breadcrumbs"
import OutOfStockBanner from "./wizard-header/out-of-stock-banner"
import Promo from "./wizard-header/promo"
import Ratings from "./wizard-header/ratings"

const GenericNotFound = loadable(() =>
  retry(() => import("app/generic-not-found"))
)
const ProductDescription = loadable(() =>
  retry(() => import("./info/product-description"))
)
const AsSeenIn = loadable(() =>
  retry(() => import("areas/landing/elements/landing-as-seen-in"))
)
const ProductFooter = loadable(() => retry(() => import("./product-footer")), {
  ssr: false,
})
const CartPopup = loadable(() => retry(() => import("areas/cart/popup")))
const CartPreviewModal = loadable(() =>
  retry(() => import("areas/cart/preview"))
)
const HonestPricingModal = loadable(() =>
  retry(() => import("./modals/honest-pricing-modal"))
)
const ReturnPolicyModal = loadable(() =>
  retry(() => import("./modals/return-policy-modal"))
)
const PreviewImageModal = loadable(() =>
  retry(() => import("./modals/preview-image-modal"))
)
// const Miniview = loadable(() => retry(() => import("./miniview")))
// const J3DViewHalfModalsWrapper = loadable(() =>
//   retry(() => import("areas/product/jewlr/modals/j3dview-half-modals-wrapper"))
// )

if (typeof window != "undefined") {
  try {
    if (window.location.hash != "") {
      var hash = window.location.hash.split("#")[1]
      if (hash.match("&gid=[0-9]*&pid=[0-9]*")) {
        window.location.hash = ""
      }
    }
  } catch (e) {
    console.log(e)
  }

  // EDGE ONLY - remove this line once Microsoft Edge decides to start supporting
  // HTMLCanvasElement.toBlob
  try {
    if (
      !Object.prototype.hasOwnProperty.call(
        HTMLCanvasElement.prototype,
        "toBlob"
      )
    ) {
      require("blueimp-canvas-to-blob")
    }
  } catch (e) {
    // Continue if this polyfill fails to load
    console.log("Couldn't load toBlob polyfill", e)
  }
}

const Thumbnail = ({
  index,
  onClick,
  productTitle,
  selected,
  src,
  srcSet,
  tabIndex,
  view,
}) => {
  const { isMobile } = useWindowDimensions()
  const keyboardProps = useNavigation({
    direction: isMobile ? "horizontal" : "vertical",
  })

  return (
    <Button
      aria-label={`Go to slide ${index + 1}`}
      className={cn(
        "hover:border-grey-5 focus:border-primary border-3 relative block aspect-square h-auto w-full focus:outline-none",
        "has-[.lazyload]:border-grey-10",
        selected ? "border-grey-10" : "border-transparent"
      )}
      onClick={onClick}
      tabIndex={tabIndex}
      {...(selected && { "aria-current": true })}
      {...keyboardProps}
    >
      <Image
        alt={productTitle}
        className={cn(
          "h-auto w-full",
          view === "tangiblee_view" &&
            (sl.config.env == "staging"
              ? "js-tangiblee-cta-v2"
              : "js-tangiblee-cta")
        )}
        data-cy={`product-main-slider-thumbnail-img-${index}`}
        height="500"
        lazyload={index > 4}
        sizes="46px"
        src={src}
        srcSet={srcSet}
        width="500"
      />
    </Button>
  )
}

Thumbnail.propTypes = {
  index: PropTypes.number,
  onClick: PropTypes.func,
  productTitle: PropTypes.string,
  selected: PropTypes.bool,
  src: PropTypes.string,
  srcSet: PropTypes.string,
  tabIndex: PropTypes.string,
  view: PropTypes.string,
}

const Thumbnails = ({
  currentSlideIndex,
  j3dSlideIndex,
  productTitle,
  slidesArray,
}) => {
  const dispatch = useDispatch()
  const wizardLoading = useSelector((state) => state.wizard.loading)
  const { isMobile } = useWindowDimensions()

  const handleThumbnailClick = useCallback(
    (index) => {
      if (!isTestMode && isMobile) {
        dataLayer.push({ event: "clicked-mobile-image-thumbnail" })
      }
      dispatch(trackSlide(index))
    },
    [dispatch, isMobile]
  )

  return (
    <Carousel
      aria-label={`View different images of ${productTitle}`}
      className="flex flex-col"
      description="Product Image Thumbnails"
      disableSkip
      opts={{
        active: true,
        axis: "x",
        breakpoints: {
          "(min-width: 768px)": {
            active: false,
            axis: "y",
          },
        },
        slidesToScroll: "auto",
      }}
    >
      <CarouselContent
        className="tablet:flex-col tablet:gap-12 tablet:pl-0 gap-8 pl-16"
        viewportClassName="w-full"
      >
        <FocusScope>
          {(wizardLoading
            ? [...Array(6)]
            : slidesArray?.length > 0
              ? slidesArray.filter(({ view }) => !isJ3dView(view))
              : []
          ).map((slide, index) => (
            <CarouselItem className="basis-52" key={`mainthumb-${index}`}>
              {wizardLoading ? (
                <Shimmer className="aspect-square" />
              ) : (
                <Thumbnail
                  index={index}
                  onClick={() => handleThumbnailClick(index)}
                  productTitle={productTitle}
                  selected={currentSlideIndex === index}
                  src={
                    isVideoView(slide.view)
                      ? require("images/product/vr/videoicon.png")
                      : slide.src
                  }
                  {...(!isVideoView(slide.view) && {
                    srcSet: slide.srcset,
                  })}
                  tabIndex={
                    currentSlideIndex === index ||
                    (currentSlideIndex === j3dSlideIndex && index === 0)
                      ? "0"
                      : "-1"
                  }
                  view={slide.view}
                />
              )}
            </CarouselItem>
          ))}
        </FocusScope>
      </CarouselContent>
    </Carousel>
  )
}

Thumbnails.propTypes = {
  currentSlideIndex: PropTypes.number,
  j3dSlideIndex: PropTypes.number,
  productTitle: PropTypes.string,
  slidesArray: PropTypes.array,
}

// let scrollTick = false

/**
 * Product index
 * @param {object} data - default data from _react.html
 */
const InternalProduct = ({
  currentSlideIndex,
  j3dSlideIndex,
  // miniview,
  // toggleMiniview,
  // toggleMiniviewTab,
  productTitle,
  slidesArray,
  staticContext,
  styleCode,
}) => {
  const imageAreaRef = useRef()
  const outOfStockRef = useRef()
  const dispatch = useDispatch()
  const outOfStock = useSelector(
    (state) => state.wizard.header?.product_out_of_stock
  )
  const productNotFound = useSelector((state) => state.wizardUI.productNotFound)
  const hasDiamondSelector = useSelector(
    (state) => state.wizard.hasDiamondSelector
  )
  // const unifiedStonePickerExp = useSelector(
  //   (state) => state.experiment.unifiedStonePicker
  // )
  const holdbackExp = useSelector(
    (state) => state.experiment.holdback.variant_id
  )
  const crossedOutExp = useSelector((state) => state.experiment.crossedOut)
  const pdpValuePropsExp = useSelector(
    (state) => state.experiment.pdpValueProps
  )
  // const wizardData = useSelector((state) => state.wizard.data)
  // const pdpWarrantyExp = useSelector((state) => state.experiment.pdpWarranty)

  const ssrNotFound =
    staticContext && styleCode === undefined && outOfStock === undefined

  // const qualifiesForUnifiedStonePicker = Boolean(
  //   (wizardData?.o1 &&
  //     wizardData.o1.title?.toLowerCase().includes("number") &&
  //     Object.keys(wizardData).some((key) => /^s\d+$/.test(key))) ||
  //     wizardData?.sc
  // )

  const handleRatingsClick = useCallback(() => {
    scrollToElement("#product-reviews", 90)
    if (!isTestMode) {
      dataLayer.push({
        event: "clicked-reviews-count-pdp",
      })
    }
  }, [])

  // Track tangiblee CTA clicks
  const handleTryItOnClick = () => {
    if (!isTestMode) {
      dataLayer.push({
        event: "clicked-try-it-on-cta-pdp",
      })
    }
  }

  // const requestScroll = () => {
  //   window.requestAnimationFrame(scrollListener)
  // }

  // const scrollListener = () => {
  //   if (!scrollTick) {
  //     scrollTick = true
  //     let scrollPos = window.scrollY

  //     if (scrollPos > 500) {
  //       if (!miniview.show) {
  //         if (!miniview.showTab) {
  //           toggleMiniviewTab(true)
  //         }
  //         if (!miniview.swipeHide) {
  //           toggleMiniview(true)
  //         }
  //       }
  //     } else {
  //       if (miniview.show) {
  //         toggleMiniview(false)
  //         toggleMiniviewTab(false)
  //       }
  //     }
  //     scrollTick = false
  //   }
  // }

  // useEffect(() => {
  //   if (unifiedStonePickerExp && qualifiesForUnifiedStonePicker) {
  //     dispatch(
  //       setExperiment({
  //         unifiedStonePicker: unifiedStonePickerExp,
  //       })
  //     )
  //   }
  // }, [unifiedStonePickerExp, qualifiesForUnifiedStonePicker])

  useEffect(() => {
    if (crossedOutExp) {
      dispatch(setExperiment({ crossedOut: crossedOutExp }))
    }
  }, [crossedOutExp])

  useEffect(() => {
    if (pdpValuePropsExp && !hasDiamondSelector) {
      dispatch(setExperiment({ pdpValueProps: pdpValuePropsExp }))
    }
  }, [hasDiamondSelector, pdpValuePropsExp])

  useEffect(() => {
    document.body.classList.add("pdp")
    return () => document.body.classList.remove("pdp")
  }, [])

  if (productNotFound || ssrNotFound) {
    return <GenericNotFound staticContext={staticContext} />
  }

  return (
    <>
      <Helmet encodeSpecialCharacters={false}>
        {/* eslint-disable react/jsx-sort-props, react/no-unknown-property */}
        <meta content="240" name="og_image_width" property="og:image:width" />
        <meta content="240" name="og_image_height" property="og:image:height" />
        {slidesArray?.length > 0 && slidesArray[0].src && (
          <meta
            content={slidesArray[0].src}
            name="og_image"
            property="og:image"
          />
        )}
        {slidesArray?.length > 0 && slidesArray[0].srcset && (
          <link
            rel="preload"
            fetchPriority="high"
            as="image"
            imageSrcSet={slidesArray[0].srcset}
            imageSizes={slidesArray[0].sizes}
          />
        )}
        {/* eslint-enable react/jsx-sort-props, react/no-unknown-property */}
      </Helmet>
      <div className="tablet:px-16 tablet:mb-10 tablet:pt-32 tablet:max-w-835 desktop:max-w-full min-[1568px]:!max-w-1440 mx-auto [--page-padding-y:32px]">
        {outOfStock && (
          <Media className="my-4 px-20" lessThan="tablet">
            <OutOfStockBanner outOfStockRef={outOfStockRef} />
          </Media>
        )}
        {holdbackExp === 0 && (
          <Media className="px-16" lessThan="tablet">
            <Breadcrumbs />
          </Media>
        )}
        {holdbackExp === 0 && (
          <Media lessThan="tablet">
            <Promo />
          </Media>
        )}
        <div
          className="tablet:items-start tablet:flex-row desktop:gap-40 flex flex-col"
          style={{
            "--sticky-top":
              "calc(var(--total-header-height) + var(--page-padding-y))",
          }}
        >
          <Media
            className="tablet:sticky tablet:top-(--sticky-top) basis-52 [grid-area:thumbnails]"
            greaterThanOrEqual="desktop"
          >
            <Thumbnails
              currentSlideIndex={currentSlideIndex}
              j3dSlideIndex={j3dSlideIndex}
              productTitle={productTitle}
              slidesArray={slidesArray}
            />
          </Media>
          <div
            className="tablet:w-1/2 tablet:sticky tablet:top-(--sticky-top) tablet:mb-0 relative mb-40 flex-1"
            ref={imageAreaRef}
          >
            <ImageAreaContent />
            {/* {isMobile && <Miniview />} */}
            {/* <J3DViewHalfModalsWrapper /> */}
          </div>
          <div
            className="tablet:w-1/2 desktop:w-480 tablet:sticky tablet:top-(--sticky-top) w-full min-w-0 px-16 [grid-area:wizard]"
            data-ge-product-container
          >
            {holdbackExp === 1 ? (
              <Breadcrumbs />
            ) : (
              <Media greaterThanOrEqual="tablet">
                <Breadcrumbs />
              </Media>
            )}
            {holdbackExp === 0 && (
              <Media lessThan="tablet">
                <div className="mb-8 flex h-32 w-full items-center justify-between">
                  <Ratings handleClick={handleRatingsClick} />
                  <div
                    className="tangiblee-text-link"
                    id="tangiblee-cta"
                    onClick={handleTryItOnClick}
                    onTouchStart={handleTryItOnClick}
                  ></div>
                </div>
              </Media>
            )}
            <MainBody
              imageAreaRef={imageAreaRef}
              outOfStockRef={outOfStockRef}
            />
          </div>
        </div>
      </div>
      <div className="desktop:max-w-1440 contained tablet:px-0 tablet:border-t tablet:border-t-grey-20">
        <ProductDescription />
      </div>
      {IS_JEWLR() && <AsSeenIn />}
      {IS_JEWLR() && <ProductFooter />}
      <SimpleDeferAfterLoadWrapper>
        <CartPopup />
        <CartPreviewModal />
        <PreviewImageModal />
        {IS_JEWLR() && <HonestPricingModal />}
        {IS_JEWLR() && <ReturnPolicyModal />}
      </SimpleDeferAfterLoadWrapper>
    </>
  )
}

InternalProduct.propTypes = {
  currentSlideIndex: PropTypes.number,
  j3dSlideIndex: PropTypes.number,
  // miniview: PropTypes.object,
  // toggleMiniview: PropTypes.func,
  // toggleMiniviewTab: PropTypes.func,
  productTitle: PropTypes.string,
  slidesArray: PropTypes.array,
  staticContext: PropTypes.object,
  styleCode: PropTypes.string,
  trackSlide: PropTypes.func,
  updateChoice: PropTypes.func,
}

const Product = fetchWishlist(InternalProduct)

export default fetchProduct(withImages(Product))
